import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Trophy, ChartBar, ArrowRight } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import axios from "axios";
import { API } from "@/App";

export default function SharedReport() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/report/${reportId}`).then(r => setReport(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [reportId]);

  if (loading) return <div className="min-h-screen bg-[#0a0a0a]"><PublicNav /><div className="max-w-3xl mx-auto px-4 py-16"><div className="h-64 skeleton rounded-sm" /></div></div>;
  if (!report) return <div className="min-h-screen bg-[#0a0a0a]"><PublicNav /><div className="max-w-3xl mx-auto px-4 py-16 text-center"><h1 className="font-heading text-3xl text-white mb-4">REPORT NOT FOUND</h1><Link to="/" className="text-[#ccff00] no-underline">Go home</Link></div></div>;

  const h = report.health || {};
  const teams = report.teams || [];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title={`${report.league_name} League Report — Dynasty Trade Maker`} description={`Competitive balance score, roster values, and team rankings for ${report.league_name}.`} path={`/report/${reportId}`} />
      <PublicNav />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#4ecdc4]">LEAGUE REPORT</span>
        <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase mt-2 mb-2" data-testid="report-title">{report.league_name}</h1>
        <p className="text-[10px] text-[#555] mb-6">Generated {new Date(report.created_at).toLocaleDateString()} · {report.total_teams} teams · Powered by Dynasty Trade Maker</p>

        {/* Balance Score */}
        <div className="border border-[#262626] bg-[#171717] p-6 text-center mb-6">
          <p className="text-[9px] font-bold uppercase text-[#555] mb-1">COMPETITIVE BALANCE SCORE</p>
          <p className={`font-mono text-6xl font-bold ${h.competitive_balance >= 70 ? "text-[#4ade80]" : h.competitive_balance >= 40 ? "text-[#fbbf24]" : "text-[#ff6b6b]"}`}>{h.competitive_balance}</p>
          <p className="text-xs text-[#888] mt-2">{h.competitive_balance >= 70 ? "Healthy league — well-balanced rosters" : h.competitive_balance >= 40 ? "Moderate imbalance — some teams significantly stronger" : "Significant imbalance — large gap between top and bottom"}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Average Value", value: h.avg_value?.toLocaleString(), color: "#fff" },
            { label: "Strongest Team", value: h.top_value?.toLocaleString(), color: "#4ade80" },
            { label: "Weakest Team", value: h.bottom_value?.toLocaleString(), color: "#ff6b6b" },
            { label: "Value Spread", value: h.value_spread?.toLocaleString(), color: "#fbbf24" },
          ].map(s => (
            <div key={s.label} className="border border-[#262626] bg-[#171717] p-3 text-center">
              <p className="font-mono text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
              <p className="text-[9px] text-[#555] uppercase">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="border border-[#262626] bg-[#171717] p-4 mb-6">
          <h3 className="text-[10px] font-bold uppercase text-[#555] mb-3">ROSTER VALUE BY TEAM</h3>
          <ResponsiveContainer width="100%" height={Math.max(200, teams.length * 32)}>
            <BarChart data={teams} layout="vertical" margin={{ left: 10, right: 20 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="owner" width={100} tick={{ fill: "#888", fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#171717", border: "1px solid #262626", fontSize: 11, color: "#fff" }} formatter={v => v.toLocaleString()} />
              <Bar dataKey="total_value" radius={[0, 3, 3, 0]}>
                {teams.map((t, i) => <Cell key={i} fill={i === 0 ? "#ccff00" : i >= teams.length - 2 ? "#ff6b6b" : "#333"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Team Table */}
        <div className="border border-[#262626] bg-[#171717] overflow-hidden mb-8">
          <div className="grid grid-cols-5 px-3 py-2 bg-[#0a0a0a] border-b border-[#262626] text-[9px] font-bold uppercase text-[#555]">
            <span className="col-span-2">Team</span>
            <span className="text-right">Value</span>
            <span className="text-right">Record</span>
            <span className="text-right">Players</span>
          </div>
          {teams.map((t, i) => (
            <div key={i} className="grid grid-cols-5 px-3 py-2 border-b border-[#1a1a1a] items-center">
              <span className="col-span-2 text-xs text-white font-semibold">{i === 0 && <Trophy size={10} weight="fill" className="text-[#ccff00] inline mr-1" />}{t.owner}</span>
              <span className="text-xs font-mono text-right text-[#ccff00]">{t.total_value?.toLocaleString()}</span>
              <span className="text-xs font-mono text-right text-[#888]">{t.wins}-{t.losses}</span>
              <span className="text-xs font-mono text-right text-[#555]">{t.player_count}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center">
          <h3 className="font-heading text-lg text-white uppercase mb-2">GET YOUR OWN LEAGUE REPORT</h3>
          <p className="text-xs text-[#888] mb-4">Import your Sleeper league and get AI-powered trade analysis, contender rankings, and more.</p>
          <Link to="/pricing" className="h-9 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold hover:bg-[#b8e600]"><ChartBar size={12} weight="fill" /> SEE PLANS <ArrowRight size={12} /></Link>
        </div>
      </div>
    </div>
  );
}
